import {
  LitElement,
  html,
  css,
  ref,
  createRef,
  utils,
  classMap,
} from '@hh/global.js'
const { throttle } = utils

class HHGallery extends LitElement {
  static properties = {
    _current: {
      state: true,
    },
    _total: {
      state: true,
    },
    desktopSpacer: {
      type: Boolean,
      attribute: 'desktop-spacer',
      reflect: true,
    },
  }

  $container = createRef()

  constructor() {
    super()

    this._total = 0
    this._current = 1

    this.throttleScroll = throttle((e) => {
      this.onScroll(e)
    }, 300)
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="image"]'))
    this._total = this.items.length
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  firstUpdated() {
    this.$container?.value?.addEventListener(
      'scroll',
      this.throttleScroll.bind(this)
    )

    this.firstImageIntersectionObserver = new IntersectionObserver(
      this.onFirstImageIntersection.bind(this),
      {
        root: this.$container?.value,
        threshold: 1,
      }
    )
    this.lastImageIntersectionObserver = new IntersectionObserver(
      this.onLastImageIntersection.bind(this),
      {
        root: this.$container?.value,
        threshold: 0.9,
      }
    )
    this.firstImageIntersectionObserver.observe(this.items[0])
    this.lastImageIntersectionObserver.observe(
      this.items[this.items.length - 1]
    )
  }

  onScroll(e) {
    this.updateUI(e)
  }

  onFirstImageIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this._current = 1
        this.updateUI()
      }
    })
  }

  onLastImageIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this._current = this._total
        this.updateUI()
      } else {
        this._current = this._total - 1
      }
    })
  }

  onPaginateClick(e) {
    e.preventDefault()
    const button = e.target.closest('button')

    const slideScrollPosition =
      button.name === 'next'
        ? this.$container?.value?.scrollLeft + this.items[0].clientWidth
        : this.$container?.value?.scrollLeft - this.items[0].clientWidth
    this.$container?.value?.scrollTo({
      left: slideScrollPosition,
    })
  }

  updateUI(e) {
    if (!this._total) return

    if (e && this._current !== this._total) {
      this.slideStopPoints.every((stopPoint, index) => {
        if (e.target.scrollLeft <= stopPoint - 100) {
          this._current = index + 1
          return false
        }
        return true
      })
    }

    this._current === 1
      ? this.prevButton?.setAttribute('disabled', true)
      : this.prevButton?.removeAttribute('disabled')

    this._current === this._total
      ? this.nextButton?.setAttribute('disabled', true)
      : this.nextButton?.removeAttribute('disabled')
  }

  moveToImage(index) {
    const currentImage = this.items[index]
    const currentImageScrollPosition = index * currentImage.clientWidth
    this.$container?.value?.scrollTo({
      left: currentImageScrollPosition,
    })
  }

  paginationTemplate() {
    if (this.slideWidthsSum <= this.$container?.value?.clientWidth) return null
    return html`
      <slot name="pagination-prev" @click="${this.onPaginateClick}"></slot>
      <slot name="pagination-next" @click="${this.onPaginateClick}"></slot>
    `
  }

  get prevButton() {
    const slot = this.shadowRoot.querySelector('slot[name="pagination-prev"]')
    return slot.assignedElements({ flatten: true })[0] ?? null
  }

  get nextButton() {
    const slot = this.shadowRoot.querySelector('slot[name="pagination-next"]')
    return slot.assignedElements({ flatten: true })[0] ?? null
  }

  get slideWidths() {
    return this.items.map((item) => {
      const { width } = item.getBoundingClientRect()
      return width
    })
  }

  get slideWidthsSum() {
    return this.slideWidths.reduce((acc, width) => acc + width, 0)
  }

  get slideStopPoints() {
    return this.slideWidths.reduce((acc, width) => {
      const last = acc[acc.length - 1] ?? 0
      acc.push(last + width)
      return acc
    }, [])
  }

  render() {
    return html`
      <div
        class="${classMap({
          wrapper: true,
          'wrapper--desktop': this.desktopSpacer,
        })}"
      >
        ${(this.desktopSpacer &&
          html`<div class="desktop-spacer" aria-hidden="true"></div>`) ||
        ''}
        <div class="images-wrapper">
          <div class="images" ${ref(this.$container)}>
            <slot name="image"></slot>
          </div>
          ${this.paginationTemplate()}
        </div>
      </div>
    `
  }

  static styles = css`
    :host {
      max-width: unset;
      display: block;
      box-sizing: border-box;
    }

    .wrapper {
      display: grid;
      margin: 2rem 0;
    }

    @media screen and (min-width: 750px) {
      .wrapper {
        margin: 4rem 0;
      }

      .wrapper--desktop {
        grid-template-columns: 1fr 10fr;
      }
    }

    .desktop-spacer {
      display: none;
    }

    @media screen and (min-width: 750px) {
      .desktop-spacer {
        display: block;
      }
    }

    .images-wrapper {
      position: relative;
      overflow-x: hidden;
    }

    .images {
      display: flex;
      align-items: flex-start;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scroll-behavior: smooth;
      scroll-padding-left: 1rem;
      -webkit-overflow-scrolling: touch;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    .images::-webkit-scrollbar {
      display: none;
    }

    .images {
      column-gap: 1rem;
      padding: 0 1rem;
    }

    @media screen and (min-width: 750px) {
      .images {
        padding: 0 1rem 0 0;
      }
    }
  `
}

customElements.define('hh-gallery', HHGallery)
